import React, { useState } from 'react';
import { useCookies } from 'react-cookie';
import { MdFilterNone } from 'react-icons/md';
import { useIntl } from 'react-intl';
import { usePosthog } from '../../providers/PosthogProvider';
import styled from 'styled-components/macro';

import { borderStyle, uiColors } from '../../common/styles';
import { useAppContext, useAuth, useCompositionTemplates, useFrami, useNavigation } from '../../context';
import ScrollHorizontalContainer from '../common/ScrollHorizontalContainer';
import FramiIconButton from '../common/FramiIconButton';
import ToolbarAnimatedHint from './ToolbarAnimatedHint';
import { useTracking } from '../../data/gtm';

const Wrapper = styled.section`
    position: relative;
    box-shadow: ${(props) => (props.$showShadow ? '0 5px 5px -5px rgba(0, 0, 0, 0.1)' : '')};
    transition: ${(props) => (props.$showShadow ? 'box-shadow 0.5s' : '')};
    z-index: 3;
    background-color: white;
`;

const OptionWrapper = styled.section`
    position: relative;
    display: block;
`;

const Option = styled(FramiIconButton)((props) => ({
    minHeight: props.$smallHeightDevice ? '35px' : '40px',
    minWidth: props.$smallHeightDevice ? '35px' : '40px',
    borderRadius: borderStyle.radius,
    boxShadow: props.selected ? borderStyle.outlineBoxShadowSelected : '',
    backgroundColor: props.selected ? uiColors.PRIMARY_0 : '',
    color: props.selected ? uiColors.LINK_TEXT : props.$available ? uiColors.SECONDARY_TEXT : uiColors.DISABLED_BORDER,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '5px auto',
}));

const CategoryIcon = styled.div`
    fill: currentColor;
    transform: scale(1.25);
    height: 20px;
    width: 20px;
    display: inherit;
`;

const Name = styled.span`
    display: block;
    opacity: ${(props) => (props.$showLabel ? 1 : 0)};
    max-height: ${(props) => (props.$showLabel ? '40px' : 0)};
    font-size: 12px;
    line-height: 1.2;
    text-align: center;
    min-width: 45px;
    color: ${(props) => (props.selected ? uiColors.LINK_TEXT : uiColors.SECONDARY_TEXT)};
    transition:
        opacity 0.3s ease,
        max-height 0.3s ease;
    overflow: hidden;
    cursor: pointer;
`;

const CategorySelect = ({ scrollPosition, filtersVisible }) => {
    const { isMobile, smallHeightDevice } = useAppContext();
    const { memoizedCategories } = useFrami();
    const { user } = useAuth();
    const { activeHint } = useNavigation();
    const { isFeatureFlagAvailable } = useAuth();
    const { filterCategoryId, setFilterCategoryId, filterCategoryAvailableCounts } = useCompositionTemplates();
    const intl = useIntl();
    const [cookies, setCookie] = useCookies(['templateCategoryClickHintSeen']);
    const [showCategoryHintSelection, setShowCategoryHintSelection] = useState(false);
    const { pushEvent } = useTracking();
    const posthog = usePosthog();

    // Convert filterCategoryAvailableCounts to an array and sort it
    const sortedCategories = React.useMemo(() => {
        if (!memoizedCategories || !filterCategoryAvailableCounts) return [];
        return Array.from(memoizedCategories.values())
            .map((category) => ({ ...category, count: filterCategoryAvailableCounts[category.id] || 0 }))
            .sort((a, b) => b.count - a.count);
    }, [memoizedCategories, filterCategoryAvailableCounts]);

    const handleSetFilterCategoryId = (id) => {
        setFilterCategoryId(id)
        if (memoizedCategories instanceof Map) {
            const category = memoizedCategories.get(filterCategoryId);
            pushEvent('filter_category', { category: category?.name ?? 'All' });
        }
    }

    return (
        <Wrapper className="joyrideStep-0" $showShadow={scrollPosition > 1 && !filtersVisible}>
            <ScrollHorizontalContainer $isMobile={isMobile}>
                <OptionWrapper key={`compositiontype-null`}>
                    <Option
                        title={'all categories'}
                        dataPh={'category all'}
                        dataCy="compositiontype-all"
                        $smallHeightDevice={smallHeightDevice}
                        selected={!filterCategoryId}
                        value={intl.formatMessage({ id: 'studio.composition.type.all' })}
                        onClick={() => handleSetFilterCategoryId(null)}
                        $available={true}
                        icon={<MdFilterNone style={{ display: 'inline' }} transform="scale(1.25)" />}
                    />
                    {!smallHeightDevice && (
                        <Name
                            onClick={() => handleSetFilterCategoryId(null)}
                            selected={!filterCategoryId}
                            $showLabel={scrollPosition <= 38}
                        >
                            {intl.formatMessage({ id: 'studio.composition.type.all' })}
                        </Name>
                    )}
                </OptionWrapper>

                {sortedCategories &&
                    Array.from(sortedCategories.values()).map((ct, idx) => {
                        if (!ct.svg_icon || ct.is_archive) {
                            return null;
                        }

                        return (
                            <OptionWrapper key={`compositiontype-${idx}`}>
                                <Option
                                    dataPh={`category ${ct.name.toLowerCase()}`}
                                    dataCy={`compositiontype-${ct.name.toLowerCase()}`}
                                    $smallHeightDevice={smallHeightDevice}
                                    selected={ct.id === filterCategoryId || (idx === 1 && showCategoryHintSelection)}
                                    value={ct}
                                    onClick={() => {
                                        handleSetFilterCategoryId(ct.id);
                                        if (!cookies.templateCategoryClickHintSeen) {
                                            setCookie('templateCategoryClickHintSeen', true, { expires: 0, path: '/' });
                                        }
                                        if (showCategoryHintSelection) {
                                            setShowCategoryHintSelection(false);
                                        }
                                    }}
                                    $available={ct.count > 0}
                                    icon={<CategoryIcon dangerouslySetInnerHTML={{ __html: ct.svg_icon }} />}
                                    title={ct.name}
                                />
                                {idx === 1 &&
                                    activeHint === 1.3 &&
                                    isFeatureFlagAvailable &&
                                    posthog.getFeatureFlag('gesture-template-click') === 'show-gestures' && (
                                        <ToolbarAnimatedHint select={setShowCategoryHintSelection} />
                                    )}
                                {!smallHeightDevice && (
                                    <Name
                                        onClick={() => handleSetFilterCategoryId(ct.id)}
                                        selected={ct.id === filterCategoryId}
                                        $showLabel={scrollPosition <= 38}
                                    >
                                        {ct.name}
                                    </Name>
                                )}
                            </OptionWrapper>
                        );
                    })}
            </ScrollHorizontalContainer>
        </Wrapper>
    );
};

export default CategorySelect;
